import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import FilterIcon from '../../assets/icons/filter-icon.svg?react';
import SearchInput from '../../components/SearchInput';
import Select from '../../components/Select';
import FilterClearIcon from '../../assets/icons/filter-clear-icon.svg?react';
import { Link, useNavigate } from 'react-router';
import WarningIcon from '../../assets/icons/warning-icon.svg?react';
import DownloadIcon from '../../assets/icons/download-icon.svg?react';

import {
  CorrectiveActionApiCorrectiveActionControllerGetAllCorrectiveActionAssignedToMeRequest,
  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMeAttentionNeededEnum,
  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMePriorityEnum,
  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMeStatusEnum,
  CorrectiveActionsStatusEnum,
  GetCorrectiveActionAssignedToMeResponse,
} from '@EHSNavigator/backend';
import { useDebounce } from '@uidotdev/usehooks';
import Loader from '../../components/Loader';
import NoItemsFound from '../../components/NoItemsFound';
import Pagination from '../../components/CustomPagination';
import moment from 'moment';
import { useClickOutside } from '../../utils/clickOutSide';
import {
  handleCorrectiveActionsAssignedToMeExportToExcel,
  handleExportToCSV,
} from '../../utils/exportTable';
import Button from '../../components/Button';

interface CorrectiveActionsAssignedToMeProps {
  otherClasses?: string;
  data: GetCorrectiveActionAssignedToMeResponse;
  noDataMessage: string;
  isIncidentsLoading: boolean;
  params: CorrectiveActionApiCorrectiveActionControllerGetAllCorrectiveActionAssignedToMeRequest;
}

const DUE_DAYS = [
  {
    label: 'Due in 1 days',
    value: '1',
  },
  {
    label: 'Due in 2 days',
    value: '2',
  },
  {
    label: 'Due in 3 days',
    value: '3',
  },
  {
    label: 'Due in 5 days',
    value: '5',
  },
  {
    label: 'Due in 7 days',
    value: '7',
  },
  {
    label: 'Due in 10 days',
    value: '10',
  },
  {
    label: 'Due in 14 days',
    value: '14',
  },
];

const CorrectiveActionsAssignedToMe: React.FC<
  CorrectiveActionsAssignedToMeProps
> = ({ otherClasses, data, noDataMessage, params, isIncidentsLoading }) => {
  const correctiveActionsAssignedToMeClasses = clsx(
    otherClasses,
    'flex flex-col flex-1 w-full',
  );

  const [toggle, setToggle] = useState(false);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });
  const [state, setState] = useState({
    page: params?.page || 1,
    search: params?.search || '',
    status: params?.status || '',
    dueDays: params?.dueInDays || '',
    priority: params?.priority || '',
    attentionNeeded: params?.attentionNeeded || '',
    limit: params?.limit || 10,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(state?.search, 600);
  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      search: debouncedSearch,
      dueDays: state.dueDays,
      status: state.status,
      attentionNeeded: state.attentionNeeded,
      priority: state.priority,
      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/assigned-to-me?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [
    debouncedSearch,
    state.dueDays,
    state.status,
    state.priority,
    state.attentionNeeded,
    state.page,
    state.limit,
  ]);

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);
    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }
    navigate(`/assigned-to-me?${params.toString()}`);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  const exportData = {
    header: [
      'Sr.',
      'Ref#',
      'Corrective Action',
      'Due Date',
      'Assigned By',
      'Priority',
      'Status',
    ],
    columnWidths: [
      { width: 5 },
      { width: 18 },
      { width: 30 },
      { width: 20 },
      { width: 30 },
      { width: 15 },
      { width: 23 },
    ],
    exportData: data?.correctiveActions?.map((incident: any, index: number) => {
      return {
        'Sr.': (state.page - 1) * state.limit + index + 1,
        'Ref#': incident.correctActionRefId,
        'Corrective Action': incident.title
          ? incident.title
              .split(' ')
              .map(
                (word: string) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(' ')
          : '',
        'Due Date': incident.dueDate,
        'Assigned By': incident.user?.fullName,
        Priority: incident.priority,
        Status: incident.status,
      };
    }),
  };

  const options = [
    {
      label: 'Export CSV',
      onClick: () => {
        handleExportToCSV(exportData?.exportData, exportData?.header);
      },
    },
    {
      label: 'Export XLSX',
      onClick: () => {
        handleCorrectiveActionsAssignedToMeExportToExcel(
          exportData?.exportData,
          exportData?.header,
          exportData?.columnWidths,
        );
      },
    },
  ];

  return (
    <div
      className={correctiveActionsAssignedToMeClasses}
      data-testid="corrective-actions-assigned-to-me"
    >
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Corrective Actions Assigned to Me</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Review all corrective actions assigned to you by your manager from
            this list and take action on them.
          </p>
          {data?.overDue !== 0 && (
            <p className="font-inter-variable text-neutral-tones-text mt-15 hidden items-center gap-1 text-xl leading-6 font-bold lg:flex">
              <WarningIcon /> {data?.overDue} Action(s) are{' '}
              <span className="text-brand-posh-pink">OVERDUE</span>
            </p>
          )}
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Review all corrective actions assigned to you by your manager from this
        list and take action on them.
      </p>
      {data?.overDue !== 0 && (
        <p className="font-inter-variable text-neutral-tones-text mt-15 flex items-center gap-1 text-lg leading-5 font-bold lg:hidden">
          <WarningIcon /> {data?.overDue} Action(s) are{' '}
          <span className="text-brand-posh-pink">OVERDUE</span>
        </p>
      )}
      <hr className="border-neutral-tones-text/50 mt-9 mb-6 lg:hidden" />
      <div className="flex items-stretch gap-2.5 lg:mt-12">
        <SearchInput
          name="search"
          onChange={(e) =>
            setState((prev) => ({
              ...prev,
              search: e?.target?.value,
            }))
          }
          placeholder="Enter title, reference ID, or department..."
          value={state?.search}
          disabled={false}
          errorMessage=""
          otherClasses={{ container: 'w-full' }}
        />
        <div ref={ref} className="relative flex lg:block">
          <Button
            onClick={() => setToggle(!toggle)}
            otherClasses="!py-0 flex-1 h-full"
            size="smaller"
            disable={data?.correctiveActions?.length === 0}
          >
            <DownloadIcon className="rotate-180 [&>g>path]:fill-current" />{' '}
            <span className="hidden md:block">Export</span>
          </Button>
          {toggle && (
            <ul
              className={clsx(
                'bg-light-translucent-background ring-neutral-tones-text/35 dark:bg-dark-translucent-background absolute top-14 right-0 z-10 flex w-fit flex-col rounded-md p-2.5 ring md:w-full lg:top-18',
              )}
            >
              {options.map((option, index) => (
                <button
                  key={index}
                  className={clsx(
                    'font-inter-variable text-neutral-tones-text border-b-neutral-tones-text/35 hover:text-brand-byzantine global-transition cursor-pointer border-b py-2 text-left text-sm font-normal whitespace-nowrap first:pt-0 last:border-b-0 last:pb-0 disabled:cursor-not-allowed disabled:text-neutral-600',
                  )}
                  onClick={() => option.onClick()}
                >
                  {option.label}
                </button>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="global-scrollbar-style flex-1 overflow-auto px-0.5">
        <div className="flex w-full min-w-6xl flex-col">
          <div className="border-b-neutral-tones-text/50 relative mt-6 mb-12 flex flex-col gap-4 border-b pb-6 lg:flex-row lg:items-center lg:gap-9">
            <p className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg">
              <button
                onClick={() => {
                  if (
                    state?.search !== '' ||
                    state?.dueDays !== '' ||
                    state?.attentionNeeded !== '' ||
                    state?.status !== '' ||
                    state?.priority !== '' ||
                    state?.limit !== 10
                  ) {
                    setState((prev) => ({
                      ...prev,
                      search: '',
                      dueDays: '',
                      status: '',
                      attentionNeeded: '',
                      priority: '',
                      limit: 10,
                    }));
                  }
                }}
                className="font-inter-variable -tracking-form-input lg:-tracking-text-area text-brand-byzantine flex cursor-pointer items-center gap-2.5 object-fill text-base leading-5 font-bold lg:text-lg"
              >
                {state?.dueDays !== '' ||
                state?.status !== '' ||
                state?.priority !== '' ||
                state?.attentionNeeded !== '' ||
                state?.search !== '' ||
                state?.limit !== 10 ? (
                  <FilterClearIcon className="[&>path]:fill-brand-byzantine h-6 w-6 lg:h-9 lg:w-9" />
                ) : (
                  <FilterIcon className="h-6 w-6 lg:h-9 lg:w-9" />
                )}
                Filters
              </button>
            </p>
            <div className="flex flex-1 items-center gap-6">
              <Select
                filterSelect
                list={Object.values(
                  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMeStatusEnum,
                )
                  .filter(
                    (status) =>
                      status !==
                      CorrectiveActionControllerGetAllCorrectiveActionAssignedToMeStatusEnum.Draft,
                  )
                  .map((status) => ({
                    label: status,
                    value: status,
                  }))}
                value={state?.status}
                placeholder="Status"
                otherClasses={{
                  container: 'w-full max-w-48',
                }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    status: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={DUE_DAYS}
                value={String(state?.dueDays)}
                placeholder="Due In"
                otherClasses={{ container: 'w-full max-w-40' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    dueDays: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.entries(
                  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMeAttentionNeededEnum,
                ).map(([, value]) => ({
                  label: value,
                  value,
                }))}
                value={state?.attentionNeeded}
                placeholder="Attention Needed"
                otherClasses={{ container: 'w-full max-w-48' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    attentionNeeded: e,
                  }))
                }
                errorMessage=""
              />
              <Select
                filterSelect
                list={Object.entries(
                  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMePriorityEnum,
                ).map(([, value]) => ({
                  label: value,
                  value,
                }))}
                value={state?.priority}
                placeholder="Priorities"
                otherClasses={{ container: 'w-full max-w-32' }}
                onSelect={(e) =>
                  setState((prev) => ({
                    ...prev,
                    priority: e,
                  }))
                }
                errorMessage=""
              />
            </div>
          </div>
        </div>
        {isIncidentsLoading ? (
          <div>
            <Loader
              variant="compact"
              otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
            />
          </div>
        ) : (
          <div className="w-full min-w-6xl">
            <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
              <h6
                className={clsx(
                  'text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 pr-6 font-bold lg:text-lg lg:leading-5',
                  data?.overDue !== 0 ? 'text-right' : 'text-left',
                )}
              >
                Sr.
              </h6>
              <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area col-span-2 hidden text-lg leading-5 font-bold lg:block">
                Ref#
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-4 font-bold lg:col-span-3 lg:text-lg lg:leading-5">
                Corrective Action
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                Due Date
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:col-span-2 lg:text-lg lg:leading-5">
                Assigned by
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Priority
              </h6>
              <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                Status
              </h6>
            </div>
            {data?.correctiveActions?.length > 0 ? (
              <>
                {data?.correctiveActions?.map(
                  (
                    {
                      title,
                      correctActionRefId,
                      dueDate,
                      user,
                      priority,
                      status,
                      id,
                    },
                    index,
                  ) => {
                    const due = moment(dueDate, 'YYYY-MM-DD');
                    const current = moment(moment().format('YYYY-MM-DD'));
                    const isDue =
                      status !== CorrectiveActionsStatusEnum?.Closed &&
                      due < current;

                    return (
                      <Link
                        to={`/assigned-to-me/${id}`}
                        key={index}
                        className="border-b-neutral-tones-text/50 grid grid-cols-12 items-center gap-4 border-b py-5"
                      >
                        <p
                          onClick={(e) => e.preventDefault()}
                          className={clsx(
                            'text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center pr-6 text-sm leading-4 font-bold lg:text-base lg:leading-5 lg:font-bold',
                            isDue
                              ? 'justify-between'
                              : data?.overDue === 0
                                ? 'justify-start'
                                : 'justify-end',
                          )}
                        >
                          {isDue && (
                            <button className="group relative">
                              <WarningIcon className="h-5 min-h-5 w-5 min-w-5" />
                              <span className="bg-neutral-tones-sidebar text-neutral-tones-text absolute bottom-6 left-0 z-10 hidden min-w-36 items-center justify-center rounded px-2 py-1 text-xs font-normal shadow-lg group-hover:block group-focus:block">
                                Due Date Exceeded
                              </span>
                            </button>
                          )}
                          {(state.page - 1) * state.limit + index + 1}
                        </p>
                        <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-2 hidden items-center text-sm leading-4 font-normal uppercase lg:flex lg:text-base lg:leading-5">
                          {correctActionRefId}
                        </p>
                        <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-4 flex items-center text-sm leading-4 font-normal capitalize lg:col-span-3 lg:text-base lg:leading-5">
                          {title}
                        </p>
                        <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                          {dueDate}
                        </p>
                        <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 flex items-center text-sm leading-4 font-normal lg:col-span-2 lg:text-base lg:leading-5">
                          {user?.fullName}
                        </p>
                        <p
                          className={clsx(
                            'font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-bold lg:text-base lg:leading-5',
                            priority ===
                              CorrectiveActionControllerGetAllCorrectiveActionAssignedToMePriorityEnum.Urgent
                              ? 'text-brand-posh-pink'
                              : priority ===
                                  CorrectiveActionControllerGetAllCorrectiveActionAssignedToMePriorityEnum.High
                                ? 'text-brand-fair-orange'
                                : priority ===
                                    CorrectiveActionControllerGetAllCorrectiveActionAssignedToMePriorityEnum.Medium
                                  ? 'text-primary-yellow'
                                  : 'text-brand-papery',
                          )}
                        >
                          {priority}
                        </p>
                        <p
                          className={clsx(
                            'font-inter-variable lg:-tracking-form-input col-span-1 flex items-center justify-between text-sm leading-4 font-bold lg:text-base lg:leading-5',
                            status === CorrectiveActionsStatusEnum?.Active
                              ? 'text-brand-posh-pink'
                              : status === CorrectiveActionsStatusEnum?.Closed
                                ? 'text-brand-papery'
                                : status ===
                                    CorrectiveActionsStatusEnum?.InReview
                                  ? 'text-brand-fair-orange'
                                  : 'text-brand-byzantine',
                          )}
                        >
                          {status}{' '}
                        </p>
                      </Link>
                    );
                  },
                )}
              </>
            ) : (
              <NoItemsFound message={noDataMessage} />
            )}
          </div>
        )}
      </div>
      {data?.correctiveActions?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.correctiveActions?.length}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default CorrectiveActionsAssignedToMe;
